import React, { useEffect, useState } from "react";
import {
  Stack,
  Box,
  Typography,
  Slider,
  FormControlLabel,
  FormGroup,
  Checkbox,
  TextField,
  Grid,
  InputAdornment,
  FormControl,
  FormHelperText,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SingleImageUpload from "../../media/singleimageupload";
import MultipleImageUpload from "../../media/multipleimageupload";
import CustomRating from "../ui/CustomRating";
import { useAuth } from "../../../contexts/AuthContext";
import { useUnits } from "../../../contexts/UnitContext";

// Glassmorphic main container
const GlassAdditionalInfoContainer = styled(Stack)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.8
  )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  borderRadius: 20,
  padding: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.3
    )}, transparent)`,
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 80%, ${alpha(
      theme.palette.primary.main,
      0.03
    )} 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${alpha(
      theme.palette.secondary.main,
      0.03
    )} 0%, transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },
}));

// Glassmorphic form control container
const GlassFormControl = styled(FormControl)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(2),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.05
  )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  backdropFilter: "blur(15px",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  borderRadius: 16,
  position: "relative",
  transition: "all 0.3s ease",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.primary.main,
      0.2
    )}, transparent)`,
  },

  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.08
    )} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
}));

// Glassmorphic slider with enhanced styling
const GlassSlider = styled(Slider)(({ theme }) => ({
  "& .MuiSlider-track": {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    border: "none",
    height: 6,
    borderRadius: 3,
    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  "& .MuiSlider-rail": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.background.paper,
      0.6
    )} 0%, ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
    backdropFilter: "blur(10px)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
    height: 6,
    borderRadius: 3,
  },
  "& .MuiSlider-thumb": {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    border: `2px solid ${alpha(theme.palette.common.white, 0.3)}`,
    width: 20,
    height: 20,
    boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.4)}`,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
      transform: "scale(1.1)",
    },
    "&::before": {
      boxShadow: "none",
    },
  },
  "& .MuiSlider-valueLabel": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.9
    )} 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
    backdropFilter: "blur(10px)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    borderRadius: 8,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
  },
}));

// Glassmorphic text field
const GlassTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.background.paper,
      0.6
    )} 0%, ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
    backdropFilter: "blur(15px)",
    WebkitBackdropFilter: "blur(15px)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
    borderRadius: 12,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",

    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg, transparent, ${alpha(
        theme.palette.primary.main,
        0.2
      )}, transparent)`,
      opacity: 0,
      transition: "opacity 0.3s ease",
    },

    "&:hover": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.background.paper,
        0.7
      )} 0%, ${alpha(theme.palette.background.paper, 0.5)} 100%)`,
      transform: "translateY(-1px)",
      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,

      "&::before": {
        opacity: 1,
      },
    },

    "&.Mui-focused": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.background.paper,
        0.8
      )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
      transform: "translateY(-2px)",

      "&::before": {
        opacity: 1,
      },
    },

    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },

    "& input": {
      color: theme.palette.text.primary,
      fontWeight: 500,
      "&::placeholder": {
        color: alpha(theme.palette.text.primary, 0.6),
      },
    },
  },

  "& .MuiInputLabel-root": {
    color: alpha(theme.palette.text.primary, 0.8),
    fontWeight: 500,
    background: "transparent",
    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
}));

// Glassmorphic image upload container
const GlassImageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.7
  )} 0%, ${alpha(theme.palette.background.paper, 0.5)} 100%)`,
  backdropFilter: "blur(20px",
  border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
  borderRadius: 16,
  position: "relative",
  transition: "all 0.3s ease",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.3
    )}, transparent)`,
    zIndex: 1,
  },

  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.background.paper,
      0.8
    )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    transform: "translateY(-1px)",
    boxShadow: `0 6px 25px ${alpha(theme.palette.common.black, 0.1)}`,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },
}));

// Glassmorphic typography for labels
const GlassTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: alpha(theme.palette.text.primary, 0.9),
  marginBottom: theme.spacing(1.5),
  textShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`,
}));

// Glassmorphic checkbox container
const GlassCheckboxContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.warning.main,
    0.08
  )} 0%, ${alpha(theme.palette.warning.main, 0.04)} 100%)`,
  backdropFilter: "blur(15px)",
  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
  borderRadius: 16,
  position: "relative",
  transition: "all 0.3s ease",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.warning.main,
      0.3
    )}, transparent)`,
  },

  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.warning.main,
      0.12
    )} 0%, ${alpha(theme.palette.warning.main, 0.06)} 100%)`,
    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.15)}`,
  },
}));

// Glassmorphic Turnstile container
const GlassTurnstileContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  minHeight: "65px",
  padding: theme.spacing(2),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.success.main,
    0.06
  )} 0%, ${alpha(theme.palette.success.main, 0.03)} 100%)`,
  backdropFilter: "blur(15px)",
  border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
  borderRadius: 16,
  position: "relative",
  transition: "all 0.3s ease",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.success.main,
      0.2
    )}, transparent)`,
  },
}));

// Glassmorphic verification complete container
const GlassVerificationComplete = styled(Box)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(2),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.success.main,
    0.1
  )} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
  backdropFilter: "blur(15px)",
  border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
  borderRadius: 16,
  color: theme.palette.success.main,
  fontWeight: 600,
  fontSize: "1rem",
  position: "relative",
  transition: "all 0.3s ease",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.success.main,
      0.4
    )}, transparent)`,
  },
}));

const AdditionalInfoStep = ({
  formState,
  setFormState,
  handleImageChange,
  handleImageDescriptionChange,
  handleMediaImagesChange,
  handleMediaImageDescriptionChange,
  isSubmitting,
  turnstileSiteKey,
  loadTurnstileScript,
  turnstileToken,
}) => {
  const { currentUser } = useAuth();
  const { unitSystem } = useUnits();

  // Convert distance for display based on unit preference
  const displayDistance = (meters) => {
    if (unitSystem === "metric") {
      return {
        value: meters,
        unit: "m",
      };
    } else {
      // Convert to feet for English units
      const feet = meters * 3.28084;
      return {
        value: feet,
        unit: "ft",
      };
    }
  };

  // Get the display value and unit for the surface swim distance
  const { value: displayValue, unit: displayUnit } = displayDistance(
    formState.surfaceSwimDistance || 0
  );

  // Initialize text value state for the surface swim distance
  const [swimDistanceText, setSwimDistanceText] = useState(
    Math.round(displayValue)
  );

  // Update text value when displayValue changes (e.g., when unit system changes)
  useEffect(() => {
    setSwimDistanceText(Math.round(displayValue));
  }, [displayValue]); // Depend on displayValue to trigger updates when units change

  // Convert back to meters for storage when user changes the slider
  const handleSurfaceSwimChange = (_, value) => {
    // Update text field when slider changes
    setSwimDistanceText(Math.round(value));

    // If in English units, convert from feet to meters before storing
    const metersValue = unitSystem === "metric" ? value : value / 3.28084;
    setFormState((prev) => ({
      ...prev,
      surfaceSwimDistance: Math.round(metersValue),
    }));
  };

  // Handle text field changes for surface swim distance
  const handleSwimDistanceTextChange = (event) => {
    const value = parseInt(event.target.value, 10);

    if (isNaN(value)) return;

    // Ensure value is within slider bounds
    const constrainedValue = Math.max(0, Math.min(value, sliderMaxValue));

    // Update text field
    setSwimDistanceText(constrainedValue);

    // Update slider and form state
    // If in English units, convert from feet to meters before storing
    const metersValue =
      unitSystem === "metric" ? constrainedValue : constrainedValue / 3.28084;
    setFormState((prev) => ({
      ...prev,
      surfaceSwimDistance: Math.round(metersValue),
    }));
  };

  const handleCheckboxChange = (event) => {
    setFormState((prev) => ({
      ...prev,
      photosUploadedAndAgreed: event.target.checked,
    }));
  };

  const handleSlogChange = (value) => {
    setFormState((prev) => ({ ...prev, slog: value }));
  };

  // Call loadTurnstileScript when component mounts
  useEffect(() => {
    console.log("AdditionalInfoStep mounted, checking Turnstile status");
    if (turnstileSiteKey && !turnstileToken) {
      console.log("No token exists, loading Turnstile script");
      loadTurnstileScript();
    }
    return () => {
      console.log("AdditionalInfoStep unmounting");
    };
  }, [loadTurnstileScript, turnstileSiteKey, turnstileToken]);

  // Calculate slider max value based on unit system
  const sliderMaxValue =
    unitSystem === "metric" ? 300 : Math.round(300 * 3.28084); // 300m or ~984ft

  return (
    <GlassAdditionalInfoContainer spacing={3}>
      <GlassFormControl>
        <GlassTypography gutterBottom>
          Surface swim distance: {Math.round(displayValue)} {displayUnit}
        </GlassTypography>
        <GlassSlider
          value={displayValue}
          onChange={handleSurfaceSwimChange}
          valueLabelDisplay="auto"
          min={0}
          max={sliderMaxValue}
          valueLabelFormat={(value) => `${Math.round(value)} ${displayUnit}`}
        />

        {/* Add text field for surface swim distance */}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <GlassTextField
                label="Surface Swim Distance"
                type="number"
                value={swimDistanceText}
                onChange={handleSwimDistanceTextChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {displayUnit}
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  min: 0,
                  max: sliderMaxValue,
                  step: 1,
                }}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </Box>

        <FormHelperText
          sx={{
            mt: 1,
            fontWeight: 500,
            color: (theme) => alpha(theme.palette.text.primary, 0.7),
          }}
        >
          {unitSystem === "metric"
            ? "Distance in meters"
            : "Distance in feet (will be stored as meters)"}
        </FormHelperText>
      </GlassFormControl>

      <Box>
        <CustomRating
          value={formState.slog}
          onChange={handleSlogChange}
          label="Slog to get to the water (difficulty reaching the entry point):"
          helperText="1 = Very difficult access, 5 = Very easy access"
        />
      </Box>

      <GlassImageContainer>
        <GlassTypography gutterBottom>
          Divesite cover image: (max 1)
        </GlassTypography>
        <SingleImageUpload
          name="heroImage"
          onImageChange={handleImageChange("heroImage")}
          value={formState.heroImage}
        />
        {formState.heroImage && (
          <Box mt={2}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: (theme) => alpha(theme.palette.text.primary, 0.8),
              }}
            >
              Add description:
            </Typography>
            <GlassTextField
              label="Cover image description"
              value={formState.heroImage?.description || ""}
              onChange={handleImageDescriptionChange("heroImage")}
              fullWidth
              margin="normal"
              variant="outlined"
              size="small"
            />
          </Box>
        )}
      </GlassImageContainer>

      <GlassImageContainer>
        <GlassTypography gutterBottom>
          Divesite diagram: (max 1)
        </GlassTypography>
        <SingleImageUpload
          name="diagramImage"
          onImageChange={handleImageChange("diagramImage")}
          value={formState.diagramImage}
        />
        {formState.diagramImage && (
          <Box mt={2}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: (theme) => alpha(theme.palette.text.primary, 0.8),
              }}
            >
              Add description:
            </Typography>
            <GlassTextField
              label="Diagram description"
              value={formState.diagramImage?.description || ""}
              onChange={handleImageDescriptionChange("diagramImage")}
              fullWidth
              margin="normal"
              variant="outlined"
              size="small"
            />
          </Box>
        )}
      </GlassImageContainer>

      <GlassImageContainer>
        <GlassTypography gutterBottom>Upload photos (max 6):</GlassTypography>
        <MultipleImageUpload
          name="mediaImages"
          onImageChange={handleMediaImagesChange}
          value={formState.mediaImages}
        />
        {formState.mediaImages && formState.mediaImages.length > 0 && (
          <Box mt={2}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: (theme) => alpha(theme.palette.text.primary, 0.8),
              }}
            >
              Add descriptions:
            </Typography>
            <Grid container spacing={2}>
              {formState.mediaImages.map((imageItem, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <GlassTextField
                    label={`Description for image ${index + 1}`}
                    value={imageItem.description}
                    onChange={(e) =>
                      handleMediaImageDescriptionChange(index, e.target.value)
                    }
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </GlassImageContainer>

      <GlassCheckboxContainer>
        <FormGroup>
          <GlassTypography gutterBottom>Copyright</GlassTypography>
          <FormControlLabel
            control={
              <Checkbox
                checked={formState.photosUploadedAndAgreed || false}
                onChange={handleCheckboxChange}
                name="photosUploadedAndAgreed"
                required
                sx={{
                  color: (theme) => alpha(theme.palette.warning.main, 0.7),
                  "&.Mui-checked": {
                    color: (theme) => theme.palette.warning.main,
                  },
                }}
              />
            }
            label={`I ${
              currentUser
                ? `(${currentUser.displayName}) certify that I took/created these photos/images/diagrams myself.`
                : "(Not logged in)"
            }`}
            sx={{
              "& .MuiFormControlLabel-label": {
                fontWeight: 500,
                color: (theme) => alpha(theme.palette.text.primary, 0.9),
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: (theme) => alpha(theme.palette.text.secondary, 0.8),
              fontWeight: 400,
              lineHeight: 1.4,
              mt: 1,
            }}
          >
            Copyright violations of any kind, including photos from other
            sources (magazines, books, web) will result in your account being
            disabled permanently. If you have explicit written permission from
            the owner of a photo you did not take, you can post it if you
            include "with permission from [source]" in the photo caption.
          </Typography>
        </FormGroup>
      </GlassCheckboxContainer>

      {/* Turnstile section */}
      {!turnstileToken ? (
        <GlassTurnstileContainer id="turnstile-container" />
      ) : (
        <GlassVerificationComplete>
          Verification complete ✓
        </GlassVerificationComplete>
      )}
    </GlassAdditionalInfoContainer>
  );
};

export default AdditionalInfoStep;
